/**
 * Диалог запроса на «анализ по списку встреч» (TM-4c1fb82b).
 *
 * Открывается при ≥1 выбранной встрече в одной из трёх точек входа:
 *   - реестр встреч (mode="free")
 *   - карточка клиента (mode="client", clientId)
 *   - карточка сотрудника (mode="manager", employeeId)
 *
 * Содержит textarea свободного запроса + кнопки готовых шаблонов (как в
 * ManagerAnalysisDialog), ПЛЮС выбор 1..3 LLM-моделей и селект
 * consensus-модели, который виден/обязателен только при >1 выбранной модели.
 * Сабмит строго по контракту `POST /api/list_analyses`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createListAnalysis, listAnalysisTemplates } from "@/api/lookups";
import {
  LLM_MODEL_NAMES,
  type LLMModelName,
  type ListAnalysisMode,
  type UUID,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { LlmModelsField } from "@/components/registries/LlmModelsField";

export interface ListAnalysisDialogProps {
  open: boolean;
  mode: ListAnalysisMode;
  employeeId?: UUID;
  clientId?: UUID;
  selectedMeetingIds: UUID[];
  onClose: () => void;
  /** Уведомление об успехе для родителя (сброс выбора + тост). */
  onSubmitted?: () => void;
}

const MAX_MEETINGS = 20;

export function ListAnalysisDialog({
  open,
  mode,
  employeeId,
  clientId,
  selectedMeetingIds,
  onClose,
  onSubmitted,
}: ListAnalysisDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [queryText, setQueryText] = useState("");
  const [pickedTemplateId, setPickedTemplateId] = useState<UUID | null>(null);
  const [models, setModels] = useState<LLMModelName[]>([]);
  const [consensusModel, setConsensusModel] = useState<LLMModelName | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  // Сброс формы при закрытии — следующее открытие начинает с чистого листа.
  useEffect(() => {
    if (!open) {
      setTitle("");
      setQueryText("");
      setPickedTemplateId(null);
      setModels([]);
      setConsensusModel(null);
      setError(null);
    }
  }, [open]);

  const templatesQ = useQuery({
    queryKey: ["analysis-templates", { activeOnly: true }],
    queryFn: () => listAnalysisTemplates({ includeInactive: false }),
    enabled: open,
  });

  const multiModel = models.length > 1;

  const submitMut = useMutation({
    mutationFn: () =>
      createListAnalysis({
        mode,
        title: title.trim() ? title.trim() : null,
        employee_id: mode === "manager" ? (employeeId ?? null) : null,
        client_id: mode === "client" ? (clientId ?? null) : null,
        meeting_ids: selectedMeetingIds,
        query_text: queryText.trim() ? queryText : null,
        template_id: pickedTemplateId,
        llm_models: models,
        // Контракт: consensus обязателен при >1 модели и ЗАПРЕЩЁН (null) при 1.
        consensus_model: multiModel ? consensusModel : null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["list-analyses"] });
      onSubmitted?.();
      onClose();
    },
    onError: () => setError(t("listAnalysis.dialog.submitError")),
  });

  function toggleModel(model: LLMModelName, checked: boolean): void {
    setModels((prev) => {
      const next = checked ? [...prev, model] : prev.filter((m) => m !== model);
      // Схлопнулись до ≤1 модели — consensus больше не применим.
      if (next.length <= 1) setConsensusModel(null);
      return next;
    });
  }

  function handleSubmit(): void {
    setError(null);
    if (
      selectedMeetingIds.length < 1 ||
      selectedMeetingIds.length > MAX_MEETINGS
    ) {
      setError(t("listAnalysis.dialog.meetingsRequired"));
      return;
    }
    if (!queryText.trim() && !pickedTemplateId) {
      setError(t("listAnalysis.dialog.queryRequired"));
      return;
    }
    if (models.length < 1 || models.length > 3) {
      setError(t("listAnalysis.dialog.modelsRequired"));
      return;
    }
    if (multiModel && consensusModel === null) {
      setError(t("listAnalysis.dialog.consensusRequired"));
      return;
    }
    submitMut.mutate();
  }

  function pickTemplate(id: UUID, text: string): void {
    setPickedTemplateId(id);
    setQueryText(text);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-h-[85vh] max-w-2xl overflow-y-auto"
        data-testid="list-analysis-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t("listAnalysis.dialog.title")}</DialogTitle>
          <DialogDescription>
            {t("listAnalysis.dialog.selectedCount", {
              count: selectedMeetingIds.length,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label
              htmlFor="la-dialog-title"
              className="mb-1 block text-sm font-medium"
            >
              {t("listAnalysis.dialog.titleLabel")}
            </label>
            <input
              id="la-dialog-title"
              type="text"
              maxLength={255}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("listAnalysis.dialog.titlePlaceholder")}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="list-analysis-title-input"
            />
          </div>

          <div>
            <label
              htmlFor="la-dialog-query"
              className="mb-1 block text-sm font-medium"
            >
              {t("listAnalysis.dialog.queryLabel")}
            </label>
            <textarea
              id="la-dialog-query"
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              placeholder={t("listAnalysis.dialog.queryPlaceholder")}
              rows={6}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="list-analysis-query-textarea"
            />
          </div>

          {templatesQ.data !== undefined && templatesQ.data.length > 0 ? (
            <div>
              <p className="mb-1 text-sm font-medium">
                {t("listAnalysis.dialog.templatesLabel")}
              </p>
              <div className="flex flex-wrap gap-2">
                {templatesQ.data.map((tpl) => (
                  <Button
                    key={tpl.id}
                    type="button"
                    variant={
                      pickedTemplateId === tpl.id ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => pickTemplate(tpl.id, tpl.prompt_text)}
                    data-testid={`list-analysis-template-${tpl.id}`}
                  >
                    {tpl.title}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}

          {templatesQ.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {t("listAnalysis.dialog.templatesLoadError")}
            </p>
          ) : null}

          <LlmModelsField
            selected={models}
            hasError={false}
            onToggle={toggleModel}
          />

          {multiModel ? (
            <div>
              <Label htmlFor="la-consensus">
                {t("listAnalysis.dialog.consensusLabel")}
              </Label>
              <select
                id="la-consensus"
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                value={consensusModel ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setConsensusModel(v === "" ? null : (v as LLMModelName));
                }}
                data-testid="list-analysis-consensus-select"
              >
                <option value="">
                  {t("listAnalysis.dialog.consensusPlaceholder")}
                </option>
                {LLM_MODEL_NAMES.filter((m) => models.includes(m)).map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("listAnalysis.dialog.consensusHint")}
              </p>
            </div>
          ) : null}

          {error !== null ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitMut.isPending}
            >
              {t("listAnalysis.dialog.cancelButton")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitMut.isPending}
              data-testid="list-analysis-submit"
            >
              {t("listAnalysis.dialog.submitButton")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
